import {useNavigate} from 'react-router'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {getIdpLabel} from '@freckle/educator-entities/ts/roster/identity-provider'
import {Checkbox} from '@freckle/educator-materials/ts/components/checkbox'
import {Table} from '@freckle/educator-materials/ts/components/table/table'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  cellFromRow,
  idAccessorFromProp,
  aligned,
  disableSortBy,
} from '@freckle/educator-materials/ts/logic/table/table'
import {Grades, Role} from '@freckle/school/ts/common/components/table/cell'
import {ApiControlledTabContainer} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TeacherTabFilteringT} from '@freckle/school/ts/manage-teachers/components/teacher-tab/'
import type {
  StudentSimplifiedT,
  SchoolSimplifiedT,
} from '@freckle/school/ts/manage-teachers/models/teacher-table-row'
import type {TeacherSelectionsT} from '@freckle/school/ts/rostering/components/clean-up-roster/remove-self-rostered-teachers/review-self-rostered-teachers'
import {tableHeaderTextMargin} from './self-rostered-teacher-table.module.scss'

/* eslint-disable react/no-unused-prop-types */
export type SelfRosteredTableRowT = {
  id: number
  name: string
  email: string
  grades: Array<number>
  school: SchoolSimplifiedT
  students: Array<StudentSimplifiedT>
  role: TeacherRole | undefined | null
  source: IdentityProviderT | undefined | null
  premiumLicenses: PremiumLicensesAttrs | undefined | null
}

type Props = {
  tableParams: TableParamsT<TeacherTabFilteringT>
  fetchTableRows: (newTableParams: TableParamsT<TeacherTabFilteringT>) => Promise<{
    links: LinksT
    rows: Array<SelfRosteredTableRowT>
    totalCount: number
  }>
  onUpdate: (newTableParams: TableParamsT<TeacherTabFilteringT>) => void
  onUpdateRowSelection: (id: number, selected: boolean) => void
  onToggleSelectAll: (selectAll: boolean, allRows: {id: number}[]) => void
  getTeacherSelections: () => TeacherSelectionsT
}

export default function SelfRosteredTeacherTable({
  tableParams,
  fetchTableRows,
  onUpdate,
  onUpdateRowSelection,
  getTeacherSelections,
  onToggleSelectAll,
}: Props) {
  const {t} = useSafeTranslation()
  const routerHistory = useNavigate()

  const {selected, totalCount, totalTeachersSelected} = getTeacherSelections()

  return (
    <ApiControlledTabContainer
      name="teacher"
      tableParams={tableParams}
      routerHistory={routerHistory}
      fetchRows={fetchTableRows}
      onUpdate={onUpdate}
      selfRosteredTeachersSelections={
        <Text style="subtitle-2">
          {t('SELF_ROSTERED_TEACHER_TABLE_SELECTED', {
            numTeachersSelected: totalTeachersSelected,
            numTotal: totalCount,
          })}
        </Text>
      }
      render={({paginationPanel: PaginationPanel}, rows) => (
        <>
          <Table
            data={rows}
            columns={[
              {
                Header: (
                  <Checkbox
                    id={`toggle-all-teachers`}
                    onChange={() => onToggleSelectAll(totalCount !== totalTeachersSelected, rows)}
                    checked={totalCount === totalTeachersSelected}
                  />
                ),
                Cell: cellFromRow(({id}: SelfRosteredTableRowT) => (
                  <Checkbox
                    id={`toggle-teacher-${id}`}
                    onChange={() => onUpdateRowSelection(id, !selected.has(id))}
                    checked={selected.has(id)}
                  />
                )),
                id: 'checkbox',
                // Force this to take up as little space as possible
                widthPx: 0,
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_NAME')}
                  </Text>
                ),
                widthPx: 130,
                ...idAccessorFromProp('name'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_EMAIL')}
                  </Text>
                ),
                widthPx: 215,
                ...idAccessorFromProp('email'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_ROLE')}
                  </Text>
                ),
                Cell: cellFromRow(({role}: SelfRosteredTableRowT) => <Role role={role} />),
                widthPx: 90,
                ...idAccessorFromProp('role'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_GRADE')}
                  </Text>
                ),
                Cell: cellFromRow(({grades}: SelfRosteredTableRowT) => <Grades grades={grades} />),
                widthPx: 56,
                ...idAccessorFromProp('grades'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_STUDENTS')}
                  </Text>
                ),
                Cell: cellFromRow(({students}: SelfRosteredTableRowT) => <>{students.length}</>),
                widthPx: 80,
                ...idAccessorFromProp('students'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_MANAGED_BY')}
                  </Text>
                ),
                Cell: cellFromRow(({source: idp}: SelfRosteredTableRowT) => (
                  <>{getIdpLabel(t, idp)}</>
                )),
                widthPx: 100,
                ...idAccessorFromProp('source'),
              },
              {
                Header: (
                  <Text style="label-1" className={tableHeaderTextMargin}>
                    {t('SELF_ROSTERED_TEACHER_TABLE_COLUMN_HEADER_SCHOOL')}
                  </Text>
                ),
                Cell: cellFromRow(({school: {name}}: SelfRosteredTableRowT) => <>{name}</>),
                widthPx: 100,
                ...idAccessorFromProp('school'),
              },
            ].map(c => ({...aligned('left'), ...c, disableSortBy}))}
          />

          <PaginationPanel components={{sizePerPageDropdown: <></>, pageList: <></>}} />
        </>
      )}
    />
  )
}
